import { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { Modal } from "../components/UI";
import { Page } from "../components/AppShell";
import CrudPanel from "../components/CrudPanel";
import EmailTemplatesSection from "../components/EmailTemplatesSection";
import PdfFieldMapper from "../components/PdfFieldMapper";
import { FileText, ListChecks, ClockCounterClockwise, ArrowCounterClockwise, ShieldCheck, Envelope, Briefcase, Gauge, Tag as TagIcon, MagnifyingGlass, Plus, Trash, FilePdf } from "@phosphor-icons/react";

export default function Templates() {
  const [tab, setTab] = useState("templates");
  const [types, setTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [historyFor, setHistoryFor] = useState(null); // {id, name}
  const [versions, setVersions] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    api.get("/template-types").then(r => setTypes(r.data));
    api.get("/users").then(r => setUsers(r.data)).catch(() => {});
  }, []);

  const openHistory = async (tpl) => {
    setHistoryFor(tpl);
    try {
      const r = await api.get(`/templates/${tpl.id}/versions`);
      setVersions(r.data);
    } catch (e) {
      setVersions([]);
      alert(formatErr(e));
    }
  };

  const restoreVersion = async (versionId) => {
    if (!confirm("Restore this version as the current body? This creates a new version snapshot at the top of the history.")) return;
    try {
      await api.post(`/templates/${historyFor.id}/versions/${versionId}/restore`, {});
      const r = await api.get(`/templates/${historyFor.id}/versions`);
      setVersions(r.data);
      setReloadKey(k => k + 1);
    } catch (e) { alert(formatErr(e)); }
  };

  return (
    <Page
      title="Templates & Affidavits"
      subtitle="DOCUMENT BOILERPLATE"
      breadcrumbs={[{ label: "Dashboard", to: "/" }, "Templates"]}
      testid="templates-page"
    >
      <div className="flex gap-2 mb-4" data-testid="templates-tabs">
        <button className={`btn btn-sm ${tab === "templates" ? "btn-primary" : "btn-ghost"}`} onClick={() => setTab("templates")} data-testid="tab-templates">
          <FileText size={12} /> TEMPLATES
        </button>
        <button className={`btn btn-sm ${tab === "email-templates" ? "btn-primary" : "btn-ghost"}`} onClick={() => setTab("email-templates")} data-testid="tab-email-templates">
          <Envelope size={12} /> EMAIL TEMPLATES
        </button>
        <button className={`btn btn-sm ${tab === "job-types" ? "btn-primary" : "btn-ghost"}`} onClick={() => setTab("job-types")} data-testid="tab-job-types">
          <Briefcase size={12} /> JOB TYPES
        </button>
        <button className={`btn btn-sm ${tab === "service-speed" ? "btn-primary" : "btn-ghost"}`} onClick={() => setTab("service-speed")} data-testid="tab-service-speed">
          <Gauge size={12} /> SERVICE SPEED PRESETS
        </button>
        <button className={`btn btn-sm ${tab === "tags" ? "btn-primary" : "btn-ghost"}`} onClick={() => setTab("tags")} data-testid="tab-tags">
          <TagIcon size={12} /> TAGS
        </button>
        <button className={`btn btn-sm ${tab === "affidavits" ? "btn-primary" : "btn-ghost"}`} onClick={() => setTab("affidavits")} data-testid="tab-affidavits">
          <ListChecks size={12} /> AFFIDAVITS
        </button>
        <button className={`btn btn-sm ${tab === "court-forms" ? "btn-primary" : "btn-ghost"}`} onClick={() => setTab("court-forms")} data-testid="tab-court-forms">
          <FilePdf size={12} /> COURT FORMS
        </button>
        <button className={`btn btn-sm ${tab === "glba-types" ? "btn-primary" : "btn-ghost"}`} onClick={() => setTab("glba-types")} data-testid="tab-glba-types">
          <ShieldCheck size={12} /> GLBA CERTIFICATION TYPES
        </button>
      </div>

      {tab === "templates" ? (
        <CrudPanel
          key={`tpl-${reloadKey}`}
          endpoint="/templates"
          testid="template"
          columns={[
            { key: "name", label: "NAME" },
            { key: "template_type_id", label: "TYPE", render: r => types.find(t => t.id === r.template_type_id)?.name || "—" },
            { key: "current_version_number", label: "VERSION", numeric: true },
            { key: "is_active", label: "ACTIVE", render: r => r.is_active ? "✓" : "—" },
          ]}
          formFields={[
            { key: "name", label: "NAME" },
            { key: "template_type_id", label: "TYPE", type: "select", options: types },
            { key: "body", label: "BODY (supports {{placeholders}})", type: "templateBody" },
            { key: "is_active", label: "Active", type: "boolean" },
          ]}
          // eslint-disable-next-line react/no-unstable-nested-components
          rowActions={(it) => <HistoryButton item={it} onClick={openHistory} />}
        />
      ) : tab === "email-templates" ? (
        <EmailTemplatesSection />
      ) : tab === "job-types" ? (
        <CrudPanel
          endpoint="/jobs/types"
          testid="job-type"
          columns={[
            { key: "name", label: "NAME" },
            { key: "apply_case", label: "APPLY CASE", render: (it) => it.apply_case ? "✓" : "—" },
            { key: "has_location_names", label: "LOCATION NAMES", render: (it) => it.has_location_names ? "✓" : "—" },
          ]}
          formFields={[
            { key: "name", label: "NAME", type: "text" },
            { key: "apply_case", label: "APPLY CASE (show Case link on Job Dash)", type: "boolean" },
            { key: "has_location_names", label: "LOCATION NAMES (allow per-location display names)", type: "boolean" },
          ]}
          defaultForm={{ name: "", apply_case: true, has_location_names: false }}
        />
      ) : tab === "service-speed" ? (
        <ServiceSpeedTab />
      ) : tab === "tags" ? (
        <TagsTab />
      ) : tab === "affidavits" ? (
        <CrudPanel
          endpoint="/affidavits"
          testid="affidavit"
          columns={[
            { key: "name", label: "NAME" },
            { key: "state", label: "STATE" },
            { key: "is_default", label: "DEFAULT", render: r => r.is_default ? "✓" : "—" },
            { key: "is_active", label: "ACTIVE", render: r => r.is_active ? "✓" : "—" },
          ]}
          formFields={[
            { key: "name", label: "NAME" },
            { key: "state", label: "STATE (2-letter)" },
            { key: "body", label: "BODY (supports {{placeholders}})", type: "templateBody" },
            { key: "is_default", label: "Default for state", type: "boolean" },
            { key: "is_active", label: "Active", type: "boolean" },
          ]}
        />
      ) : tab === "court-forms" ? (
        <CourtFormsTab />
      ) : (
        // GLBA Certification Types — lookup table of compliance certifications
        // a user can attach to a job to confirm permissible-purpose. Configured
        // once, then selected by name on each job's GLBA panel.
        <div data-testid="glba-types-panel">
          <p className="text-xs text-muted-foreground mb-3">
            Set up the GLBA certification types your team uses (e.g. <em>FCRA — Locate</em>, <em>Insurance Claim</em>, <em>Collection</em>). Once defined, they appear as a dropdown when adding a GLBA verification to a job.
          </p>
          <CrudPanel
            endpoint="/glba-certification-types"
            testid="glba-cert-type"
            columns={[
              { key: "name", label: "NAME" },
              { key: "description", label: "DESCRIPTION" },
              { key: "is_active", label: "ACTIVE", render: r => r.is_active ? "✓" : "—" },
            ]}
            formFields={[
              { key: "name", label: "NAME" },
              { key: "description", label: "DESCRIPTION", type: "textarea" },
              { key: "is_active", label: "Active", type: "boolean" },
            ]}
          />
        </div>
      )}

      <Modal
        open={!!historyFor}
        onClose={() => { setHistoryFor(null); setVersions([]); }}
        title={historyFor ? `Version history — ${historyFor.name}` : ""}
        footer={<button className="btn btn-secondary" onClick={() => setHistoryFor(null)}>CLOSE</button>}
      >
        {versions.length === 0 ? (
          <div className="text-sm text-muted-foreground p-4" data-testid="empty-versions">
            No versions yet. Edit the template body to create snapshots automatically.
          </div>
        ) : (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto" data-testid="versions-list">
            {versions.map((v, idx) => (
              <div key={v.id} className="surface p-3" data-testid={`version-row-${v.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="badge badge-default mono">v{v.version_number}</span>
                    {idx === 0 && <span className="badge badge-green">CURRENT</span>}
                    <span className="text-xs text-muted-foreground">
                      {v.created_at ? new Date(v.created_at).toLocaleString() : ""}
                    </span>
                    {v.created_by && (
                      <span className="text-xs text-muted-foreground">
                        by {users.find(u => u.id === v.created_by)?.name || v.created_by.slice(0, 8)}
                      </span>
                    )}
                  </div>
                  {idx !== 0 && (
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => restoreVersion(v.id)}
                      data-testid={`restore-version-${v.id}`}
                    >
                      <ArrowCounterClockwise size={12} /> RESTORE
                    </button>
                  )}
                </div>
                <pre className="text-xs bg-muted/30 p-2 rounded whitespace-pre-wrap max-h-40 overflow-y-auto">{v.body || "(empty)"}</pre>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </Page>
  );
}

function HistoryButton({ item, onClick }) {
  return (
    <button
      className="btn btn-ghost btn-sm mr-1"
      onClick={() => onClick(item)}
      title="View version history"
      data-testid={`template-history-${item.id}`}
    >
      <ClockCounterClockwise size={12} /> HISTORY
    </button>
  );
}


/* iter 40 — Service Speed Preset editor (filtered per Job Type). */
function ServiceSpeedTab() {
  const [jobTypes, setJobTypes] = useState([]);
  const [activeJobType, setActiveJobType] = useState("");

  useEffect(() => {
    api.get("/jobs/types").then(r => {
      const items = r.data || [];
      setJobTypes(items);
      if (items.length && !activeJobType) setActiveJobType(items[0].id);
    });
  }, []);

  if (jobTypes.length === 0) {
    return (
      <div className="surface p-6 text-sm text-muted-foreground" data-testid="service-speed-empty">
        Create at least one <strong>Job Type</strong> first, then come back here to add Service Speed presets that belong to it.
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-3" data-testid="service-speed-type-tabs">
        <span className="label-xs">JOB TYPE:</span>
        {jobTypes.map(jt => (
          <button
            key={jt.id}
            className={`btn btn-sm ${activeJobType === jt.id ? "btn-primary" : "btn-ghost"}`}
            onClick={() => setActiveJobType(jt.id)}
            data-testid={`service-speed-jt-${jt.id}`}
          >
            {jt.name}
          </button>
        ))}
      </div>
      <CrudPanel
        key={activeJobType}
        endpoint={`/service-speed-presets?job_type_id=${activeJobType}`}
        testid="service-speed-preset"
        columns={[
          { key: "name", label: "NAME" },
          { key: "days_out_for_soft_due_date", label: "SOFT DUE (DAYS)", numeric: true },
          { key: "days_out_for_hard_due_date", label: "HARD DUE (DAYS)", numeric: true },
        ]}
        formFields={[
          { key: "job_type_id", type: "hidden" },
          { key: "name", label: "NAME", type: "text" },
          { key: "days_out_for_soft_due_date", label: "DAYS OUT — SOFT DUE", type: "number" },
          { key: "days_out_for_hard_due_date", label: "DAYS OUT — HARD DUE", type: "number" },
        ]}
        defaultForm={{ name: "", days_out_for_soft_due_date: "", days_out_for_hard_due_date: "", job_type_id: activeJobType }}
      />
    </div>
  );
}

/* iter 41 — Unified Tags manager (single table, category filter + global search).
 * Categories supported today: customer, vendor, job, location.
 * is_system tags can be edited (color/category) but not deleted. */
const TAG_CATEGORIES = ["customer", "vendor", "job", "location"];

function TagsTab() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");           // "" = all
  const [editing, setEditing] = useState(null); // null | {id?, name, color, category}
  const [err, setErr] = useState("");

  const load = () => {
    api.get("/tags").then(r => setItems(r.data || [])).catch(e => setErr(formatErr(e)));
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter(t => {
    if (cat && t.category !== cat) return false;
    if (q && !(t.name || "").toLowerCase().includes(q.trim().toLowerCase())) return false;
    return true;
  });

  const save = async () => {
    setErr("");
    try {
      const body = { name: editing.name.trim(), color: editing.color || null, category: editing.category };
      if (!body.name) { setErr("Name is required"); return; }
      if (!TAG_CATEGORIES.includes(body.category)) { setErr("Pick a category"); return; }
      if (editing.id) await api.patch(`/tags/${editing.id}`, body);
      else await api.post("/tags", body);
      setEditing(null); load();
    } catch (e) { setErr(formatErr(e)); }
  };

  const remove = async (t) => {
    if (t.is_system) return;
    if (!confirm(`Delete tag "${t.name}"? Any rows using it will be untagged.`)) return;
    try { await api.delete(`/tags/${t.id}`); load(); }
    catch (e) { alert(formatErr(e)); }
  };

  return (
    <div data-testid="tags-tab">
      {/* Search + category filter + New button */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            className="input pl-9"
            placeholder="Search tag name…"
            value={q}
            onChange={e => setQ(e.target.value)}
            data-testid="tags-search"
          />
        </div>
        <select className="input" value={cat} onChange={e => setCat(e.target.value)} data-testid="tags-category-filter">
          <option value="">All categories</option>
          {TAG_CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
        </select>
        <button className="btn btn-primary" onClick={() => setEditing({ name: "", color: "#16a34a", category: cat || "customer" })} data-testid="new-tag-btn">
          <Plus size={14} /> NEW TAG
        </button>
      </div>

      <div className="surface">
        {filtered.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground text-sm" data-testid="empty-tags">
            {items.length === 0 ? "No tags yet. Click NEW TAG to add one." : "No tags match the current filters."}
          </div>
        ) : (
          <table className="dense">
            <thead><tr><th>NAME</th><th>CATEGORY</th><th>COLOR</th><th>SYSTEM</th><th></th></tr></thead>
            <tbody>
              {filtered.map(t => (
                <tr key={t.id} data-testid={`tag-row-${t.id}`}>
                  <td>
                    <button className="link-cell font-semibold" onClick={() => setEditing({ id: t.id, name: t.name, color: t.color || "", category: t.category || "customer", is_system: t.is_system })} data-testid={`tag-row-open-${t.id}`}>
                      <span className="badge" style={t.color ? { background: t.color, color: "#fff", borderColor: t.color } : undefined}>
                        {t.name}
                      </span>
                    </button>
                  </td>
                  <td className="text-xs">{t.category || "—"}</td>
                  <td className="mono text-xs">{t.color || "—"}</td>
                  <td className="text-xs">{t.is_system ? "✓" : "—"}</td>
                  <td className="text-right">
                    {!t.is_system && (
                      <button className="btn btn-ghost btn-sm" onClick={() => remove(t)} data-testid={`tag-delete-${t.id}`} title="Delete">
                        <Trash size={12} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add / Edit form (simple inline modal) */}
      {editing && (
        <Modal open onClose={() => setEditing(null)} title={editing.id ? "Edit tag" : "New tag"} testid="tag-edit-modal"
          footer={<><button className="btn btn-secondary" onClick={() => setEditing(null)}>CANCEL</button><button className="btn btn-primary" onClick={save} data-testid="save-tag-btn">SAVE</button></>}>
          <div className="space-y-3">
            <div>
              <div className="label-xs mb-1">NAME</div>
              <input className="input" value={editing.name} onChange={e => setEditing(s => ({ ...s, name: e.target.value }))} data-testid="tag-name-input" />
            </div>
            <div>
              <div className="label-xs mb-1">CATEGORY</div>
              <select className="input" value={editing.category} onChange={e => setEditing(s => ({ ...s, category: e.target.value }))} data-testid="tag-category-select">
                {TAG_CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <div className="label-xs mb-1">COLOR</div>
              <div className="flex items-center gap-2">
                <input type="color" value={editing.color || "#16a34a"} onChange={e => setEditing(s => ({ ...s, color: e.target.value }))} className="w-12 h-9 rounded" data-testid="tag-color-picker" />
                <input className="input mono flex-1" value={editing.color || ""} onChange={e => setEditing(s => ({ ...s, color: e.target.value }))} placeholder="#16a34a" data-testid="tag-color-input" />
              </div>
            </div>
            {editing.is_system && (
              <p className="text-xs text-amber-600">This is a system tag — it can be edited but not deleted.</p>
            )}
            {err && <div className="text-xs text-destructive">{err}</div>}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ===========================================================================
// iter 43 — Court Forms tab (PDF-overlay affidavit templates).
// ===========================================================================
function CourtFormsTab() {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(null); // template being edited or null
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setItems((await api.get("/affidavit-templates")).data || []); }
    catch (e) { setErr(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      await api.post("/affidavit-templates", { name: newName.trim() });
      setNewName("");
      await load();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this court form template?")) return;
    try { await api.delete(`/affidavit-templates/${id}`); await load(); }
    catch (e) { setErr(formatErr(e)); }
  };

  return (
    <div data-testid="court-forms-panel">
      <p className="text-xs text-muted-foreground mb-3">
        Upload the official state/federal court PDF for each affidavit you file
        (e.g. CA POS-010, NY AOS). Drop placeholder boxes on the PDF (page +
        x/y) and they&apos;ll be filled with live attempt data on every served
        service. <strong>The drag-and-drop visual mapper ships in the next
        release</strong> — for now you can paste the JSON field map directly.
      </p>

      <div className="flex gap-2 mb-4">
        <input
          className="input flex-1"
          placeholder="New form name (e.g. CA POS-010 Proof of Service)"
          value={newName}
          onChange={e => setNewName(e.target.value)}
          data-testid="court-form-new-name"
        />
        <button className="btn btn-primary" onClick={create} disabled={busy || !newName.trim()} data-testid="court-form-new-btn">
          <Plus size={14} /> NEW FORM
        </button>
      </div>

      {err && <div className="text-xs text-destructive mb-2">{err}</div>}

      <table className="table w-full text-sm" data-testid="court-forms-table">
        <thead>
          <tr>
            <th className="text-left">NAME</th>
            <th className="text-left">PDF</th>
            <th className="text-left">FIELDS</th>
            <th className="text-left">NOTARY</th>
            <th className="text-left">DEFAULT</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {items.map(t => (
            <tr key={t.id} data-testid={`court-form-row-${t.id}`}>
              <td>
                <button type="button" className="link" onClick={() => setEditing(t)} data-testid={`court-form-name-${t.id}`}>
                  {t.name}
                </button>
              </td>
              <td>
                {t.template_pdf_url
                  ? <a href={t.template_pdf_url} target="_blank" rel="noreferrer" className="link">view PDF ↗</a>
                  : <span className="text-muted-foreground">— not uploaded —</span>}
              </td>
              <td>{(t.fields_json || []).length}</td>
              <td>{t.notary_required ? "✓" : "—"}</td>
              <td>{t.is_default ? "✓" : "—"}</td>
              <td className="text-right">
                <button className="btn btn-ghost btn-sm" onClick={() => remove(t.id)} data-testid={`court-form-delete-${t.id}`}>
                  <Trash size={14} />
                </button>
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr><td colSpan={6} className="text-center py-6 text-muted-foreground" data-testid="court-forms-empty">
              No court forms yet. Add one above.
            </td></tr>
          )}
        </tbody>
      </table>

      {editing && (
        <CourtFormEditor
          template={editing}
          onClose={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function CourtFormEditor({ template, onClose }) {
  const [t, setT] = useState(template);
  const [fieldsText, setFieldsText] = useState(JSON.stringify(template.fields_json || [], null, 2));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // iter 43 Phase 2 — visual mapper toggle (defaults to visual when a PDF is uploaded).
  const [mode, setMode] = useState(template.template_pdf_url ? "visual" : "json");
  const [fields, setFields] = useState(template.fields_json || []);

  // Keep JSON textarea + visual state in sync.
  const onFieldsChange = (next) => {
    setFields(next);
    setFieldsText(JSON.stringify(next, null, 2));
  };
  const onJsonText = (txt) => {
    setFieldsText(txt);
    try { setFields(JSON.parse(txt || "[]")); }
    catch { /* keep last good `fields`; error surfaces on save */ }
  };

  const save = async () => {
    setBusy(true); setErr("");
    try {
      let nextFields;
      try { nextFields = mode === "json" ? JSON.parse(fieldsText || "[]") : fields; }
      catch { throw new Error("fields JSON is not valid — fix and retry"); }
      const r = await api.put(`/affidavit-templates/${t.id}`, {
        name: t.name,
        description: t.description || "",
        notary_required: !!t.notary_required,
        is_default: !!t.is_default,
        fields_json: nextFields,
      });
      setT(r.data);
      setFields(r.data.fields_json || []);
      setFieldsText(JSON.stringify(r.data.fields_json || [], null, 2));
    } catch (e) { setErr(formatErr(e) || e.message); }
    finally { setBusy(false); }
  };

  const uploadPdf = async (file) => {
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/affidavit-templates/${t.id}/upload-pdf`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setT(r.data);
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title={`Edit — ${t.name}`} testid="court-form-editor">
      <div className="space-y-3" style={{ minWidth: 520 }}>
        <div>
          <label className="text-xs font-semibold uppercase">Name</label>
          <input className="input" value={t.name} onChange={e => setT(s => ({ ...s, name: e.target.value }))} data-testid="court-form-edit-name" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase">Description</label>
          <input className="input" value={t.description || ""} onChange={e => setT(s => ({ ...s, description: e.target.value }))} data-testid="court-form-edit-desc" />
        </div>
        <div className="flex gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!t.notary_required} onChange={e => setT(s => ({ ...s, notary_required: e.target.checked }))} data-testid="court-form-edit-notary" />
            Notary required
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!t.is_default} onChange={e => setT(s => ({ ...s, is_default: e.target.checked }))} data-testid="court-form-edit-default" />
            Default for org
          </label>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase">Official court PDF</label>
          <div className="flex items-center gap-2">
            <input type="file" accept="application/pdf" onChange={e => uploadPdf(e.target.files?.[0])} data-testid="court-form-edit-pdf-file" />
            {t.template_pdf_url && (
              <a href={t.template_pdf_url} target="_blank" rel="noreferrer" className="link">view ↗</a>
            )}
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-semibold uppercase">Field map</label>
            <div className="flex gap-1">
              <button type="button"
                className={`btn btn-sm ${mode === "visual" ? "btn-primary" : "btn-ghost"}`}
                onClick={() => setMode("visual")}
                disabled={!t.template_pdf_url}
                title={t.template_pdf_url ? "" : "Upload a PDF first"}
                data-testid="court-form-mode-visual"
              >VISUAL</button>
              <button type="button"
                className={`btn btn-sm ${mode === "json" ? "btn-primary" : "btn-ghost"}`}
                onClick={() => setMode("json")}
                data-testid="court-form-mode-json"
              >JSON</button>
            </div>
          </div>
          {mode === "visual" && t.template_pdf_url ? (
            <PdfFieldMapper
              pdfUrl={t.template_pdf_url}
              fields={fields}
              onChange={onFieldsChange}
            />
          ) : (
            <>
              <p className="text-xs text-muted-foreground mb-1">
                Each entry: <code>{`{ "id":"<stable>", "page":0, "x":72, "y":540, "width":220, "height":14, "font_size":10, "kind":"text|signature|photo|qr|checkbox", "data_path":"attempt.recipient_name" }`}</code>
              </p>
              <textarea
                className="input mono"
                rows={10}
                value={fieldsText}
                onChange={e => onJsonText(e.target.value)}
                data-testid="court-form-edit-fields"
              />
            </>
          )}
        </div>
        {err && <div className="text-xs text-destructive">{err}</div>}
        <div className="flex justify-end gap-2">
          <button className="btn btn-ghost" onClick={onClose} data-testid="court-form-edit-cancel">CANCEL</button>
          <button className="btn btn-primary" onClick={save} disabled={busy} data-testid="court-form-edit-save">
            {busy ? "SAVING…" : "SAVE"}
          </button>
        </div>
      </div>
    </Modal>
  );
}


